import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchPortfolioCompositionReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { PortfolioCompositionReportApiItem } from '../../types/report';
import { gridFormatDecimalString, gridFormatDecimalString4 } from './reportGridFormatters';
import { useReportCsvExport } from './useReportCsvExport';

function portfolioCompositionRowId(row: PortfolioCompositionReportApiItem) {
  return `${row.subscribed_portfolio_id}-${row.fund_id}`;
}

/** Target composition of every subscribed portfolio: one row per portfolio and fund, every status included. */
export function PortfolioCompositionReportPage() {
  const columns = useRef<GridColDef[]>([
    { field: 'subscribed_portfolio_id', headerName: 'Subscription Id', type: 'number', width: 130 },
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 100 },
    { field: 'portfolio_name_en', headerName: 'Portfolio', minWidth: 180, flex: 0.3, sortable: false },
    { field: 'portfolio_status', headerName: 'Portfolio status', width: 140 },
    { field: 'product_type', headerName: 'Product type', minWidth: 160, flex: 0.2 },
    { field: 'portfolio_currency', headerName: 'Currency', width: 100, sortable: false },
    { field: 'template_portfolio_id', headerName: 'Template Id', type: 'number', width: 110, sortable: false },
    { field: 'fund_id', headerName: 'Fund Id', type: 'number', width: 100 },
    { field: 'fund_name_en', headerName: 'Fund', minWidth: 200, flex: 0.3, sortable: false },
    { field: 'fund_code', headerName: 'Fund code', width: 130, sortable: false },
    { field: 'fund_weight_pct', headerName: 'Weight %', width: 110, valueFormatter: gridFormatDecimalString },
    {
      field: 'fund_cost_base',
      headerName: 'Cost base',
      width: 130,
      sortable: false,
      valueFormatter: gridFormatDecimalString4,
    },
    { field: 'fund_nav', headerName: 'NAV', width: 120, sortable: false, valueFormatter: gridFormatDecimalString4 },
    { field: 'fund_asset_class', headerName: 'Asset class', minWidth: 160, flex: 0.2, sortable: false },
    { field: 'fund_status', headerName: 'Fund status', width: 120, sortable: false },
    { field: 'fund_currency', headerName: 'Fund currency', width: 120, sortable: false },
    { field: 'portfolio_total_weight', headerName: 'Total weight', width: 120, sortable: false },
    { field: 'portfolio_fund_count', headerName: 'Funds', width: 90, sortable: false },
    { field: 'portfolio_created_at', headerName: 'Created at', width: 200 },
    { field: 'portfolio_archived_at', headerName: 'Archived at', width: 180, sortable: false },
  ]);

  const {
    portfolioCompositionReportItems,
    totalPortfolioCompositionReportItems,
    fetchPortfolioCompositionReportStatus,
    error,
  } = useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'portfolio-composition-report.csv',
    useCallback(() => reportService.exportPortfolioCompositionReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Portfolio composition</Typography>
          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export CSV
          </LoadingButton>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchPortfolioCompositionReport}
          rows={portfolioCompositionReportItems}
          columns={columns.current}
          getRowId={portfolioCompositionRowId}
          loading={fetchPortfolioCompositionReportStatus === fetchStatuses.pending}
          rowCount={totalPortfolioCompositionReportItems}
          initialSortModel={[{ field: 'subscribed_portfolio_id', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
